// src/components/BankPosting/csvImportUtils.js
// ============================================================================
// BLOCK BP.CSV.001 — Bank CSV parsing helpers for bank transaction import
// ----------------------------------------------------------------------------
// PURPOSE
// - Parses local bank CSV export files entirely in-browser.
// - Normalizes date + amount + text fields required by backend matching.
// - Produces compact row payloads for backend insert + dedupe processing.
// ============================================================================

function trimStr(value) {
  return String(value ?? '').trim();
}

function compactWhitespace(value) {
  return trimStr(value).replace(/\s+/g, ' ');
}

function normalizeHeader(value) {
  return trimStr(value)
    .replace(/^\uFEFF/, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function parseCsvRecords(text) {
  const rows = [];
  let currentCell = '';
  let currentRow = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }

      currentRow.push(currentCell);
      currentCell = '';

      if (currentRow.some((value) => value !== '')) {
        rows.push(currentRow);
      }

      currentRow = [];
      continue;
    }

    currentCell += char;
  }

  if (currentCell.length || currentRow.length) {
    currentRow.push(currentCell);
    if (currentRow.some((value) => value !== '')) {
      rows.push(currentRow);
    }
  }

  return rows;
}

function parseCurrency(value) {
  const raw = trimStr(value);

  if (!raw) {
    return Number.NaN;
  }

  const normalized = raw.replace(/[$,\s]/g, '').replace(/^\((.*)\)$/, '-$1');
  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : Number.NaN;
}

function formatDateOnlyFromLocalParts(parsed) {
  const year = String(parsed.getFullYear()).padStart(4, '0');
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseCsvDate(value) {
  const raw = trimStr(value);

  if (!raw) {
    return '';
  }

  const mmddyyyyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+.*)?$/);
  if (mmddyyyyMatch) {
    const month = mmddyyyyMatch[1].padStart(2, '0');
    const day = mmddyyyyMatch[2].padStart(2, '0');
    const rawYear = mmddyyyyMatch[3];
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month}-${day}`;
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return formatDateOnlyFromLocalParts(parsed);
  }

  return '';
}

function resolveHeaderIndexMap(headerRow) {
  const headerMap = new Map();

  headerRow.forEach((headerValue, index) => {
    headerMap.set(normalizeHeader(headerValue), index);
  });

  return headerMap;
}

function readCell(row, headerMap, keys) {
  const sourceKeys = Array.isArray(keys) ? keys : [keys];

  for (const key of sourceKeys) {
    const index = headerMap.get(key);
    if (index !== undefined) {
      return row[index] ?? '';
    }
  }

  return '';
}

function buildNormalizedImportRow(row, headerMap, rowNumber) {
  const postDateRaw = compactWhitespace(
    readCell(row, headerMap, ['postdate', 'date', 'posteddate'])
  );
  const date = parseCsvDate(postDateRaw);
  const amount = parseCurrency(readCell(row, headerMap, 'amount'));
  const description = compactWhitespace(readCell(row, headerMap, 'description'));
  const text = compactWhitespace(readCell(row, headerMap, ['text', 'fulltext', 'details']));
  const reference = compactWhitespace(readCell(row, headerMap, 'reference'));
  const additionalReference = compactWhitespace(
    readCell(row, headerMap, ['additionalreference', 'secondaryreference'])
  );
  const accountNumber = compactWhitespace(readCell(row, headerMap, 'accountnumber'));

  if (!date || !Number.isFinite(amount) || (!description && !text)) {
    return null;
  }

  return {
    row_number: rowNumber,
    post_date_raw: postDateRaw,
    date,
    amount,
    description,
    text,
    reference,
    additional_reference: additionalReference,
    account_number: accountNumber,
  };
}

export async function parseBankTransactionCsvFile(file) {
  const text = (await file.text()).replace(/^\uFEFF/, '');
  const rows = parseCsvRecords(text);

  if (!rows.length) {
    return [];
  }

  const [headerRow, ...dataRows] = rows;
  const headerMap = resolveHeaderIndexMap(headerRow);

  return dataRows
    .map((row, index) => buildNormalizedImportRow(row, headerMap, index + 2))
    .filter(Boolean);
}
